import { PreferenceCenter } from "@ua/web-sdk/lib/remote-data/preference-center"
import * as React from "react"
import { parse as parseQueryString } from "querystring"

import { EmailPreferenceCenterClient } from "../clients/client"
import { EmailPreferenceCenterEntry } from "../../bootstrap"
import {
  DeviceType,
  getChannelDetails,
  isContactPreferenceCenter,
  resolveContactId,
} from "../utils"
import { createEmailUnsubscribeUrl } from "../../utils"
import { EmailOptOut } from "./types"

type LoadedState = {
  status: "loaded"
  preferenceCenter: PreferenceCenter
  channelId: string
  deviceType: DeviceType
  contactId: string
  isContactPreferenceCenter: boolean
  unsubscribe: EmailOptOut
}

type LoadingState = {
  status: "loading"
}

type ErrorState = {
  status: "error"
  error: Error
}

type LoadDataOpts = {
  preferenceCenterClient: EmailPreferenceCenterClient
  preferenceCenterId: string
  channelId: string
  token: string
}

type State = LoadedState | LoadingState | ErrorState

export default function usePreferenceCenter(
  entry: EmailPreferenceCenterEntry
): State {
  const [state, updateState] = React.useState<State>({ status: "loading" })
  const channelId: string = React.useMemo(() => {
    const { channel_id: channelId } = parseQueryString(
      window.location.search.slice(1)
    )
    if (typeof channelId !== "string") throw new Error("Invalid channel ID")
    return channelId
  }, [])
  const preferenceCenterClient = React.useMemo(
    () => new EmailPreferenceCenterClient(entry.auth.secret, entry.auth.key),
    [entry.auth]
  )
  React.useEffect(() => {
    loadData({
      preferenceCenterClient,
      preferenceCenterId: entry.id,
      channelId,
      token: entry.auth.secret,
    })
      .then((data) => {
        updateState({ ...data, channelId })
      })
      .catch((error) => {
        updateState({ status: "error", error })
      })
  }, [])

  return state

  async function loadData({
    preferenceCenterClient,
    preferenceCenterId,
    channelId,
    token,
  }: LoadDataOpts): Promise<Omit<LoadedState, "channelId">> {
    const { device_type: deviceType } = await getChannelDetails(
      channelId,
      token
    )
    if (deviceType !== "email") {
      throw new Error(`Unsupported operation for device of type: ${deviceType}`)
    }

    const [preferenceCenter, contactId] = await Promise.all([
      preferenceCenterClient.getPreferenceCenter(
        preferenceCenterId,
        deviceType
      ),
      resolveContactId(channelId, deviceType, token),
    ])
    if (!preferenceCenter) {
      throw new Error("Preference center not found")
    }

    const unsubscribeUrl = createEmailUnsubscribeUrl({
      appKey: entry.auth.key,
      channelId,
      redirectUrl: entry.unsubscribeRedirectUrl,
    })

    const unsubscribe = async () => {
      window.location.href = unsubscribeUrl
    }

    return {
      status: "loaded",
      preferenceCenter,
      isContactPreferenceCenter: isContactPreferenceCenter(preferenceCenter),
      contactId,
      deviceType,
      unsubscribe,
    }
  }
}
